import { useMemo } from 'react'
import { visibleQuestionIds, type AnswerValue, type SurveyQuestion } from 'shared'
import { Card } from '../ui'
import QuestionField from './QuestionField'

/**
 * The questions a person is shown: the ones whose conditions hold, with the
 * headings that group them. What is not asked is not rendered at all, and not
 * merely hidden — a required field that cannot be seen must not be able to stop a
 * submit, and the rule that says so is `visibleQuestionIds`, the one the server
 * applies too.
 */
export default function QuestionList({
  questions,
  answers,
  onChange,
  errors,
  disabled,
  readOnly,
}: {
  questions: SurveyQuestion[]
  answers: Record<string, AnswerValue>
  onChange: (questionId: string, value: AnswerValue) => void
  errors?: Record<string, string | undefined>
  disabled?: boolean
  readOnly?: boolean
}) {
  const asked = useMemo(() => visibleQuestionIds(questions, answers), [questions, answers])

  return (
    <Card className="divide-y divide-gray-200 dark:divide-gray-800">
      {questions
        .filter((question) => asked.has(question.id))
        .map((question) =>
          question.type === 'section' ? (
            <QuestionField key={question.id} question={question} value={null} onChange={() => undefined} />
          ) : (
            <div key={question.id} className="p-6">
              <QuestionField
                question={question}
                value={answers[question.id] ?? null}
                onChange={(value) => onChange(question.id, value)}
                error={errors?.[question.id]}
                disabled={disabled}
                readOnly={readOnly}
              />
            </div>
          ),
        )}
    </Card>
  )
}
